import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  GKModal,
  GKSearchSelect,
  GKSelect,
  GKTextArea,
  GKTextField,
} from "components";
import { FieldArray, Form, Formik } from "formik";
import { useState } from "react";
import toast from "react-hot-toast";
import { RiDeleteBinLine } from "react-icons/ri";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { CORPORATE_ACTION_TYPE } from "utils/constants";
import { QueryKeys } from "utils/queryKey";

interface Props {
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  refetch?: any;
  companyDetails: any;
}

function AddCorporateActionModal(props: Props) {
  const { isModalOpen, setIsModalOpen, refetch, companyDetails }: any = props;
  const { id } = useParams();

  const { instType } = companyDetails;

  const [perentInstrument, setPerentInstrument] = useState([]);

  const type = instType ? [instType] : [];

  const { isFetching } = useQuery({
    queryKey: [QueryKeys.CORPORATE_ACTION_COMPANY_LIST, instType],
    queryFn: () =>
      axiosInstance.get(`/instruments/list/`, {
        params: {
          inst_type:
            type.length > 0
              ? `[${type.map((t) => `"${t}"`).join(",")}]`
              : undefined,
        },
      }),
    enabled: !!instType,
    onSuccess: (data) => {
      setPerentInstrument(
        data.data.data?.map((item: any) => ({
          name: item.companyName,
          value: item.gridCode,
          isin: item.isin,
        }))
      );
    },
  });

  return (
    <GKModal
      open={isModalOpen}
      setOpen={setIsModalOpen}
      modalTitle={"Add New Corporate Action"}
      footer={
        <Grid2 container justifyContent={"flex-end"} gap={1}>
          <Button variant="outlined" onClick={() => setIsModalOpen(false)}>
            Close
          </Button>
          <Button type="submit" form="corporate-action-form" variant="outlined">
            Submit
          </Button>
        </Grid2>
      }
    >
      <Formik
        initialValues={{
          act_type: "",
          act_date: "",
          per_share: "",
          for_every: "",
          new_share_number: "",
          bonus_amount: "",
          existing_asset_quantity: "",
          new_asset_quantity: "",
          parent_instrument: "",
          cost_acquisition_ratio: "",
          re_gcode: "",
          premium: "",
          purpose: "",
          closing_date: "",
          isCeil: false,
          demerged_companies: [
            {
              instrument: "",
              new_asset_quantity: "",
              cost_acquisition_ratio: "",
            },
          ],
        }}
        onSubmit={async (values) => {
          await axiosInstance
            .post(`/admin-instrument-ca/${id}/create`, values)
            .then((res) => {
              toast.success(res.data.message);
              refetch();
              setIsModalOpen(false);
            })
            .catch((error) => {
              toast.error(
                error?.response?.data?.message || "Something went wrong"
              );
            });
        }}
      >
        {({ values, handleChange, handleBlur, setFieldValue }) => {
          return (
            <Form id="corporate-action-form">
              <Grid2 container spacing={2}>
                <Grid2 container size={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isCeil"
                        checked={values.isCeil}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="Ceil Fraction Holdings"
                  />
                </Grid2>
                <Grid2 container size={12} spacing={2}>
                  <Grid2 size={6}>
                    <FormControl variant="standard" fullWidth>
                      <GKSelect
                        inputLabel="Action Type"
                        onChange={handleChange}
                        value={values.act_type}
                        name="act_type"
                      >
                        {CORPORATE_ACTION_TYPE?.map((dataItem, i) => (
                          <MenuItem key={i} value={dataItem.value}>
                            {dataItem.name}
                          </MenuItem>
                        ))}
                      </GKSelect>
                    </FormControl>
                  </Grid2>
                  <Grid2 size={6}>
                    <FormControl variant="standard" fullWidth>
                      <GKTextField
                        type="date"
                        name="act_date"
                        // inputLabel="Action Date"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.act_date}
                      />
                    </FormControl>
                  </Grid2>
                </Grid2>
                {values.act_type === "DIV" && (
                  <Grid2 container size={12} spacing={2}>
                    <Grid2 size={6}>
                      <FormControl variant="standard" fullWidth>
                        <GKTextField
                          type="number"
                          name="per_share"
                          inputLabel="Per Share"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.per_share}
                        />
                      </FormControl>
                    </Grid2>
                  </Grid2>
                )}
                {(values.act_type === "SPL" ||
                  values.act_type === "RIGHTS" ||
                  values.act_type === "RVS") && (
                  <Grid2 container size={12} spacing={2}>
                    <Grid2 size={6}>
                      <FormControl variant="standard" fullWidth>
                        <GKTextField
                          type="number"
                          name="for_every"
                          inputLabel="For Every"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.for_every}
                        />
                      </FormControl>
                    </Grid2>
                    <Grid2 size={6}>
                      <FormControl variant="standard" fullWidth>
                        <GKTextField
                          type="number"
                          name="new_share_number"
                          inputLabel="New Share Number"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.new_share_number}
                        />
                      </FormControl>
                    </Grid2>
                  </Grid2>
                )}
                {values.act_type === "BNS" && (
                  <Grid2 container size={12} spacing={2}>
                    <Grid2 size={6}>
                      <FormControl variant="standard" fullWidth>
                        <GKTextField
                          type="number"
                          name="for_every"
                          inputLabel="For Every"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.for_every}
                        />
                      </FormControl>
                    </Grid2>
                    <Grid2 size={6}>
                      <FormControl variant="standard" fullWidth>
                        <GKTextField
                          type="number"
                          name="bonus_amount"
                          inputLabel="Bonus Amount"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.bonus_amount}
                        />
                      </FormControl>
                    </Grid2>
                  </Grid2>
                )}
                {values.act_type === "MRG" && (
                  <>
                    <Grid2 container size={12} spacing={2}>
                      <Grid2 size={6}>
                        <FormControl variant="standard" fullWidth>
                          <GKTextField
                            type="number"
                            name="existing_asset_quantity"
                            inputLabel="Existing Asset Quantity"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.existing_asset_quantity}
                          />
                        </FormControl>
                      </Grid2>
                      <Grid2 size={6}>
                        <FormControl variant="standard" fullWidth>
                          <GKTextField
                            type="number"
                            name="new_asset_quantity"
                            inputLabel="New Asset Quantity"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.new_asset_quantity}
                          />
                        </FormControl>
                      </Grid2>
                    </Grid2>
                    <Grid2 container size={12} spacing={2}>
                      <Grid2 size={6}>
                        <GKSearchSelect
                          inputLabel="Parent Instrument"
                          disabled={isFetching}
                          options={perentInstrument || []}
                          getOptionLabel={(option) => option?.name || ""}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder="Select Company Name"
                            />
                          )}
                          fullWidth
                          onChange={(e, val) => {
                            setFieldValue("parent_instrument", val.value);
                          }}
                          value={
                            perentInstrument.filter(
                              (data: any) => data?.gcode === values.re_gcode
                            )[0]?.name
                          }
                        />
                      </Grid2>
                    </Grid2>
                  </>
                )}
                {values.act_type === "DMG" && (
                  <>
                    <Grid2 container size={12} spacing={2}>
                      <Grid2 size={6}>
                        <FormControl variant="standard" fullWidth>
                          <GKTextField
                            type="number"
                            name="existing_asset_quantity"
                            inputLabel="Existing Asset Quantity"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.existing_asset_quantity}
                          />
                        </FormControl>
                      </Grid2>
                      <Grid2 size={6}>
                        <FormControl variant="standard" fullWidth>
                          <GKTextField
                            type="number"
                            name="new_asset_quantity"
                            inputLabel="New Asset Quantity"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.new_asset_quantity}
                          />
                        </FormControl>
                      </Grid2>
                    </Grid2>
                    <Grid2 container size={12} spacing={2}>
                      <Grid2 size={6}>
                        <FormControl variant="standard" fullWidth>
                          <GKTextField
                            type="number"
                            name="cost_acquisition_ratio"
                            inputLabel="Cost Acquisition Ratio"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.cost_acquisition_ratio}
                          />
                        </FormControl>
                      </Grid2>
                    </Grid2>
                    <Grid2 size={12}>
                      <Divider />
                    </Grid2>

                    <FieldArray name="demerged_companies">
                      {({ remove, push }) => (
                        <>
                          <Grid2
                            container
                            mt={2}
                            display={"flex"}
                            justifyContent={"flex-end"}
                          >
                            <Grid2>
                              <Button
                                onClick={() =>
                                  push({
                                    instrument: "",
                                    new_asset_quantity: "",
                                    cost_acquisition_ratio: "",
                                  })
                                }
                                size="large"
                                variant="outlined"
                              >
                                Add Stock
                              </Button>
                            </Grid2>
                          </Grid2>
                          {values.demerged_companies.map(
                            (dataItem: any, index: any) => {
                              return (
                                <>
                                  <Grid2
                                    container
                                    size={12}
                                    display={"flex"}
                                    alignItems={"center"}
                                  >
                                    <Grid2 size={6}>
                                      <Typography
                                        fontWeight={500}
                                        fontSize={18}
                                        // color={theme.palette.error.main}
                                      >
                                        Add Company
                                      </Typography>
                                    </Grid2>
                                    <Grid2
                                      size={6}
                                      display={"flex"}
                                      justifyContent={"end"}
                                    >
                                      <IconButton
                                        disabled={
                                          values.demerged_companies?.length ===
                                          1
                                        }
                                        color="error"
                                        onClick={() => remove(index)}
                                      >
                                        <RiDeleteBinLine />
                                      </IconButton>
                                    </Grid2>
                                  </Grid2>
                                  <Grid2 container size={12} spacing={2}>
                                    <Grid2 size={6}>
                                      <GKSearchSelect
                                        inputLabel="Child Instrument"
                                        disabled={isFetching}
                                        options={perentInstrument || []}
                                        getOptionLabel={(option) =>
                                          option.name || option
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            variant="outlined"
                                            placeholder="Select Company Name"
                                          />
                                        )}
                                        fullWidth
                                        onChange={(event, val) => {
                                          setFieldValue(
                                            `demerged_companies.${index}.instrument`,
                                            val.value
                                          );
                                        }}
                                        value={
                                          perentInstrument?.find(
                                            (data) =>
                                              data?.value ===
                                              values.demerged_companies[index]
                                                .instrument
                                          )?.name
                                        }
                                      />
                                    </Grid2>
                                    <Grid2 size={6}>
                                      <FormControl variant="standard" fullWidth>
                                        <GKTextField
                                          type="number"
                                          name={`demerged_companies.${index}.new_asset_quantity`}
                                          inputLabel="New Asset Quantity Child"
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          value={
                                            values.demerged_companies[index]
                                              .new_asset_quantity
                                          }
                                        />
                                      </FormControl>
                                    </Grid2>
                                  </Grid2>
                                  <Grid2
                                    container
                                    size={12}
                                    spacing={2}
                                    display={"flex"}
                                    alignItems={"center"}
                                  >
                                    <Grid2 size={6}>
                                      <FormControl variant="standard" fullWidth>
                                        <GKTextField
                                          type="number"
                                          name={`demerged_companies.${index}.cost_acquisition_ratio`}
                                          inputLabel="Cost Acquisition Ratio Child"
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          value={
                                            values.demerged_companies[index]
                                              .cost_acquisition_ratio
                                          }
                                        />
                                      </FormControl>
                                    </Grid2>
                                  </Grid2>
                                </>
                              );
                            }
                          )}
                        </>
                      )}
                    </FieldArray>
                  </>
                )}
                {(values.act_type === "RIGHTS" ||
                  values.act_type === "REC") && (
                  <Grid2 container size={12} spacing={2}>
                    <Grid2 size={6}>
                      <GKSearchSelect
                        inputLabel={
                          values.act_type === "RIGHTS"
                            ? "Right Issue Instrument"
                            : "Partly Paid Instrument"
                        }
                        disabled={isFetching}
                        options={perentInstrument || []}
                        getOptionLabel={(option) => option.name || option}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder="Select Company Name"
                          />
                        )}
                        fullWidth
                        onChange={(e, val) => {
                          setFieldValue("re_gcode", val.value);
                        }}
                        value={
                          perentInstrument.filter(
                            (data: any) => data?.gcode === values.re_gcode
                          )[0]?.name
                        }
                      />
                    </Grid2>
                    <Grid2 size={6}>
                      <FormControl variant="standard" fullWidth>
                        <GKTextField
                          type="number"
                          name="premium"
                          inputLabel="Premium"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.premium}
                        />
                      </FormControl>
                    </Grid2>
                  </Grid2>
                )}

                {values.act_type === "REC" && (
                  <Grid2 size={6}>
                    <FormControl variant="standard" fullWidth>
                      <GKTextField
                        type="date"
                        name="closing_date"
                        // inputLabel="Closing Date"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.closing_date}
                      />
                    </FormControl>
                  </Grid2>
                )}

                <Grid2 size={12}>
                  <GKTextArea
                    minRows={5}
                    inputLabel="Purpose"
                    name="purpose"
                    value={values.purpose}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />{" "}
                </Grid2>
              </Grid2>
            </Form>
          );
        }}
      </Formik>
    </GKModal>
  );
}

export default AddCorporateActionModal;
